// @flow
import DashboardTableRow from "components/DashboardTableRow";
import usePermissions from "hooks/usePermissions";
import get from "lodash/get";
import isNil from "lodash/isNil";
import * as React from "react";
import type { Message } from "types/Message";
import type { UpdateResponseQueue } from "types/response_queue";
import Dropdown, { type DropdownItem } from "ui/atoms/Dropdown";
import Icons from "ui/Icons";

import SentCol from "./messageRowFragments/SentCol";
type Props = {
  message: Message,
  onUpdateEmailPress: (message: Message) => void,
  fetchingMessages: boolean,
  deleteMessage: (
    messageId: string,
    isPacket: boolean,
    responseId: string,
  ) => Promise<void>,
  onSendReminder: (message: Message) => Promise<void>,
  removingMessage: ?string,
  updateResponseQueue: (data: UpdateResponseQueue) => Promise<void>,
  archiveResponse: (
    messageId: string,
    responseId: string,
    isPacket: boolean,
  ) => Promise<void>,
};

function MessageRow({
  message,
  onUpdateEmailPress,
  fetchingMessages,
  deleteMessage,
  onSendReminder,
  removingMessage,
  updateResponseQueue,
  archiveResponse,
}: Props): React.Node {
  const { canSendResponse, canDeleteResponse, canArchiveResponse } =
    usePermissions();
  const isPacket = !isNil(message?.packet_response?.packet?.id);
  const responseKey = isPacket ? "packet_response" : "response";
  const responseId = get(message, [responseKey, "id"], null);

  const getResponseLinkInfo = React.useCallback(() => {
    let resourceName = "";
    let to = "";

    if (message?.response?.form?.id) {
      resourceName = message.response.form.name;
      to = `response/${message.response.id}`;
    }

    if (message.packet_response?.packet?.id) {
      resourceName = message.packet_response.packet.name;
      to = `response/${message?.packet_response.id}?response_type=packet`;
    }

    if (message.response?.document?.id) {
      resourceName = message.response.document.name;
      to = `response/${message.response.id}`;
    }

    return [to, resourceName];
  }, [message]);

  const dropdownItems = React.useMemo(() => {
    const items: DropdownItem[] = [];

    if (canDeleteResponse) {
      items.push({
        key: "delete",
        label: (
          <button
            className="block w-full text-center px-4 py-2 text-sm leading-5 text-txt-emphasis hover:bg-gray-100 focus:outline-none transition duration-150 ease-in-out"
            onClick={() => {
              deleteMessage(
                message.id,
                isPacket,
                isPacket
                  ? (message.packet_response?.id ?? "")
                  : (message.response?.id ?? ""),
              );
            }}
          >
            Delete
          </button>
        ),
      });
    }

    if (canSendResponse) {
      items.push({
        key: "send-reminder",
        label: (
          <button
            className="block w-full text-center px-4 py-2 text-sm leading-5 text-txt-emphasis hover:bg-gray-100 focus:outline-none transition duration-150 ease-in-out"
            onClick={() => {
              onSendReminder(message);
            }}
          >
            Send reminder
          </button>
        ),
      });

      if (canArchiveResponse) {
        items.push({
          key: "archive",
          label: (
            <button
              className="block w-full text-center px-4 py-2 text-sm leading-5 text-txt-emphasis hover:bg-gray-100 focus:outline-none transition duration-150 ease-in-out"
              onClick={() => {
                archiveResponse(message.id, responseId, isPacket);
              }}
            >
              Archive
            </button>
          ),
        });
      }
    }

    return items;
  }, [
    archiveResponse,
    canArchiveResponse,
    canDeleteResponse,
    canSendResponse,
    deleteMessage,
    isPacket,
    message,
    onSendReminder,
    responseId,
  ]);

  if (isNil(message?.id)) return null;

  const [responseLink, responseName] = getResponseLinkInfo();

  return (
    <DashboardTableRow
      customer={message?.customer}
      disabled={removingMessage === message?.id}
      popup={
        canDeleteResponse && canSendResponse ? (
          <Dropdown items={dropdownItems}>
            <button
              className={`flex m-auto items-center ${
                fetchingMessages
                  ? "text-gray-200"
                  : "text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
              }`}
            >
              <Icons.ChevronDown inheritColor size={4} />
            </button>
          </Dropdown>
        ) : null
      }
      resourceLink={responseLink}
      resourceName={responseName}
      status={
        <SentCol
          message={message}
          onClickUpdate={() => onUpdateEmailPress(message)}
        />
      }
    />
  );
}

export default MessageRow;
